import { addDays, toDateInput } from './format';

/**
 * Saved views store a page's filter state. Dates are the one awkward part: a
 * view saved as "the last 30 days" should still mean the last 30 days next
 * month. So when a view is saved with rolling dates, each date is stored as
 * an offset in days from the day it was saved, and turned back into a date
 * when the view is opened.
 */

export const DATE_KEYS = ['from', 'to', 'appointmentFrom', 'appointmentTo'] as const;

const RELATIVE = '__relativeDates';

// Calendar days are compared as plain dates (in UTC arithmetic), so neither a
// clock change nor the display time zone can shift a saved offset by a day.
function parseDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Today, as a calendar day in the dashboard's display time zone. */
function todayDay(): string {
  return toDateInput(new Date());
}

function today(): number {
  return parseDay(todayDay())!;
}

/** The filter state as it is saved. */
export function encodeView<T extends object>(filters: T, rolling: boolean): Record<string, unknown> {
  const saved: Record<string, unknown> = { ...(filters as Record<string, unknown>) };
  if (!rolling) return saved;
  const offsets: Record<string, number> = {};
  for (const key of DATE_KEYS) {
    const value = saved[key];
    if (typeof value !== 'string' || !value) continue;
    const day = parseDay(value);
    if (day === null) continue;
    offsets[key] = Math.round((day - today()) / 86_400_000);
  }
  saved[RELATIVE] = offsets;
  return saved;
}

/** A saved view back as filter state, laid over the page's defaults. */
export function decodeView<T extends object>(saved: Record<string, unknown>, defaults: T): T {
  const result: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const [key, value] of Object.entries(saved)) {
    if (key === RELATIVE || !(key in result)) continue;
    result[key] = value;
  }
  const offsets = saved[RELATIVE];
  if (offsets && typeof offsets === 'object') {
    for (const [key, offset] of Object.entries(offsets as Record<string, unknown>)) {
      if (typeof offset === 'number' && key in result) {
        result[key] = addDays(todayDay(), offset);
      }
    }
  }
  return result as T;
}

export function isRolling(saved: Record<string, unknown>): boolean {
  return Boolean(saved[RELATIVE]);
}

/** Order-insensitive comparison of two filter states. */
export function sameFilters(a: object, b: object): boolean {
  return stable(a) === stable(b);
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).sort().join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => `${key}:${stable((value as Record<string, unknown>)[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/** A short description of a rolling range, e.g. "last 30 days". */
export function describeRange(saved: Record<string, unknown>): string | null {
  const offsets = saved[RELATIVE] as Record<string, number> | undefined;
  if (!offsets || typeof offsets.from !== 'number' || typeof offsets.to !== 'number') return null;
  const { from, to } = offsets;
  if (to === 0 && from === 0) return 'today';
  if (to === 0 && from < 0) return `last ${1 - from} days`;
  if (from === 0 && to > 0) return `next ${to} days`;
  if (from < 0 && to > 0) return `${-from} days back, ${to} ahead`;
  return null;
}
